import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, LineChart, Gauge, Users, Star, CheckCircle2, MessageSquare, Clock, Minus } from 'lucide-react';
import { advancedReportsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Loader2 } from 'lucide-react';

interface AggregatedReportsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface AggregatedData {
  executive_summary: {
    total_conversations: number;
    resolution_rate: number;
    avg_response_time: number;
    customer_satisfaction: number;
    agent_utilization: number;
    period_change: {
      conversations: number;
      resolution_rate: number;
      response_time: number;
      satisfaction: number;
    };
  };
  performance_benchmarks: {
    industry_comparison: {
      resolution_rate: { current: number; industry_avg: number };
      response_time: { current: number; industry_avg: number };
      satisfaction: { current: number; industry_avg: number };
    };
    goals_tracking: {
      resolution_rate: { target: number; current: number };
      response_time: { target: number; current: number };
      satisfaction: { target: number; current: number };
    };
  };
  departmental_breakdown: Array<{
    department: string;
    agent_count: number;
    conversations_handled: number;
    avg_resolution_time: number;
    satisfaction_score: number;
    efficiency_score: number;
  }>;
  trend_analysis: {
    conversation_volume_trend: Array<{
      period: string;
      volume: number;
      growth_rate: number;
    }>;
    key_insights: string[];
    recommendations: string[];
  };
  roi_metrics: {
    cost_per_conversation: number;
    agent_productivity_score: number;
    customer_retention_impact: number;
    estimated_savings: number;
  };
}

const ExecutiveMetricCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactElement;
  suffix?: string;
}> = ({ title, value, change, icon, suffix = '' }) => (
  <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      {React.cloneElement(icon, { className: 'h-4 w-4 text-muted-foreground' })}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">
        {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </div>
      {change !== undefined && (
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          {change > 0 ? (
            <TrendingUp className="h-3 w-3" />
          ) : change < 0 ? (
            <TrendingDown className="h-3 w-3" />
          ) : (
            <Minus className="h-3 w-3" />
          )}
          {change > 0 ? '+' : ''}{change.toFixed(1)}% vs last period
        </p>
      )}
    </CardContent>
  </Card>
);

const BenchmarkCard: React.FC<{
  title: string;
  current: number;
  benchmark: number;
  unit: string;
  isHigherBetter?: boolean;
}> = ({ title, current, benchmark, unit, isHigherBetter = true }) => {
  const isAbove = isHigherBetter ? current >= benchmark : current <= benchmark;
  const percentageDiff = ((current - benchmark) / benchmark) * 100;
  const progressValue = Math.min((current / benchmark) * 100, 100);

  return (
    <Card className="bg-muted/30">
      <CardContent className="pt-4 space-y-2">
        <p className="text-sm font-medium">{title}</p>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold">
            {current.toFixed(1)}{unit}
          </span>
          <span className="text-xs text-muted-foreground">
            Target: {benchmark.toFixed(1)}{unit}
          </span>
        </div>
        <Progress value={progressValue} className="h-1.5" />
        <p className="text-xs text-muted-foreground">
          {percentageDiff > 0 ? '+' : ''}{percentageDiff.toFixed(1)}% vs benchmark
          <Badge variant={isAbove ? 'secondary' : 'outline'} className="ml-2 text-xs">
            {isAbove ? 'On target' : 'Below target'}
          </Badge>
        </p>
      </CardContent>
    </Card>
  );
};

const getEfficiencyVariant = (score: number): 'secondary' | 'outline' | 'destructive' => {
  if (score >= 80) return 'secondary';
  if (score >= 60) return 'outline';
  return 'destructive';
};

export const AggregatedReports: React.FC<AggregatedReportsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aggregatedData, setAggregatedData] = useState<AggregatedData | null>(null);

  useEffect(() => {
    fetchAggregatedData();
  }, [dateRange]);

  const fetchAggregatedData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0],
      };

      const data = await advancedReportsAPI.getAggregatedMetrics(params);
      setAggregatedData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch aggregated reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const validateData = (data: any) => ({
    executive_summary: data?.executive_summary || {},
    performance_benchmarks: data?.performance_benchmarks || { goals_tracking: {}, industry_comparison: {} },
    departmental_breakdown: data?.departmental_breakdown || [],
    trend_analysis: data?.trend_analysis || { conversation_volume_trend: [], key_insights: [], recommendations: [] },
    roi_metrics: data?.roi_metrics || {},
  });

  const safeData = validateData(aggregatedData);

  const getExecutiveSummary = () => safeData.executive_summary;
  const getPeriodChange = () => safeData.executive_summary?.period_change || {};
  const getPerformanceBenchmarks = () => safeData.performance_benchmarks;
  const getDepartmentalBreakdown = () => safeData.departmental_breakdown;
  const getTrendAnalysis = () => safeData.trend_analysis;
  const getROIMetrics = () => safeData.roi_metrics;

  if (!aggregatedData) {
    return (
      <Alert className="m-2">
        <AlertDescription>No aggregated data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  if (!safeData.executive_summary || !safeData.performance_benchmarks) {
    return (
      <Alert className="m-2">
        <AlertDescription>Incomplete data received from server. Some sections may not display properly.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">

      {/* Executive Summary Metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <ExecutiveMetricCard
          title="Total Conversations"
          value={getExecutiveSummary()?.total_conversations || 0}
          change={getPeriodChange()?.conversations}
          icon={<MessageSquare />}
        />
        <ExecutiveMetricCard
          title="Resolution Rate"
          value={(getExecutiveSummary()?.resolution_rate || 0).toFixed(1)}
          change={getPeriodChange()?.resolution_rate}
          icon={<CheckCircle2 />}
          suffix="%"
        />
        <ExecutiveMetricCard
          title="Avg Response Time"
          value={(getExecutiveSummary()?.avg_response_time || 0).toFixed(1)}
          change={getPeriodChange()?.response_time}
          icon={<Clock />}
          suffix=" min"
        />
        <ExecutiveMetricCard
          title="Customer Satisfaction"
          value={(getExecutiveSummary()?.customer_satisfaction || 0).toFixed(1)}
          change={getPeriodChange()?.satisfaction}
          icon={<Star />}
          suffix="/5.0"
        />
      </div>

      {/* Performance Benchmarks */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Performance Benchmarks & Goals</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <BenchmarkCard
              title="Resolution Rate"
              current={getPerformanceBenchmarks()?.goals_tracking?.resolution_rate?.current || 0}
              benchmark={getPerformanceBenchmarks()?.goals_tracking?.resolution_rate?.target || 90}
              unit="%"
              isHigherBetter={true}
            />
            <BenchmarkCard
              title="Response Time"
              current={getPerformanceBenchmarks()?.goals_tracking?.response_time?.current || 0}
              benchmark={getPerformanceBenchmarks()?.goals_tracking?.response_time?.target || 30}
              unit=" min"
              isHigherBetter={false}
            />
            <BenchmarkCard
              title="Customer Satisfaction"
              current={getPerformanceBenchmarks()?.goals_tracking?.satisfaction?.current || 0}
              benchmark={getPerformanceBenchmarks()?.goals_tracking?.satisfaction?.target || 4.5}
              unit="/5.0"
              isHigherBetter={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Departmental Performance */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Departmental Performance Breakdown</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Agents</TableHead>
                <TableHead className="text-right">Conversations</TableHead>
                <TableHead className="text-right">Avg Resolution Time</TableHead>
                <TableHead className="text-right">Satisfaction</TableHead>
                <TableHead className="text-right">Efficiency Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getDepartmentalBreakdown().map((dept: any, index: number) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {dept?.department || 'Unknown Department'}
                  </TableCell>
                  <TableCell className="text-right text-sm">{dept?.agent_count || 0}</TableCell>
                  <TableCell className="text-right text-sm">
                    {(dept?.conversations_handled || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {(dept?.avg_resolution_time || 0).toFixed(1)} min
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    <span className="flex items-center justify-end gap-1">
                      <Star className="h-3 w-3 text-muted-foreground" />
                      {(dept?.satisfaction_score || 0).toFixed(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={getEfficiencyVariant(dept?.efficiency_score || 0)}>
                      {(dept?.efficiency_score || 0).toFixed(0)}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ROI + Key Insights */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* ROI & Business Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">ROI & Business Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Cost per Conversation</span>
              <span className="text-sm font-bold">
                ${(getROIMetrics()?.cost_per_conversation || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Agent Productivity Score</span>
              <span className="text-sm font-bold">
                {(getROIMetrics()?.agent_productivity_score || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Customer Retention Impact</span>
              <span className="text-sm font-bold">
                {(getROIMetrics()?.customer_retention_impact || 0).toFixed(1)}%
              </span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Estimated Savings</span>
              <span className="text-2xl font-bold">
                ${(getROIMetrics()?.estimated_savings || 0).toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Key Insights & Recommendations */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <LineChart className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Key Insights & Recommendations</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-medium mb-2">Key Insights</p>
              <div className="space-y-1">
                {(getTrendAnalysis()?.key_insights || []).map((insight: string, index: number) => (
                  <p key={index} className="text-sm text-muted-foreground">
                    • {insight}
                  </p>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium mb-2">Recommendations</p>
              <div className="space-y-1">
                {(getTrendAnalysis()?.recommendations || []).map((rec: string, index: number) => (
                  <p key={index} className="text-sm text-muted-foreground">
                    • {rec}
                  </p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversation Volume Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Conversation Volume Trend Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Volume</TableHead>
                <TableHead className="text-right">Growth Rate</TableHead>
                <TableHead className="text-right">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(getTrendAnalysis()?.conversation_volume_trend || []).map((trend: any, index: number) => (
                <TableRow key={index}>
                  <TableCell className="text-sm">
                    {trend?.period || `Period ${index + 1}`}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {(trend?.volume || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    {(trend?.growth_rate || 0) > 0 ? '+' : ''}
                    {(trend?.growth_rate || 0).toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right">
                    {(trend?.growth_rate || 0) > 0 ? (
                      <TrendingUp className="h-4 w-4 text-muted-foreground ml-auto" />
                    ) : (trend?.growth_rate || 0) < 0 ? (
                      <TrendingDown className="h-4 w-4 text-muted-foreground ml-auto" />
                    ) : (
                      <span className="text-xs text-muted-foreground">Stable</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
